import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Panel, FormControl, Button } from 'react-bootstrap';
import html2canvas from 'html2canvas';

import Utils from '../util/Utils.jsx';
import { useStateValue } from '../context/state.jsx';
import {
	addLayout,
	setLayout,
	saveLayout,
	renameLayout,
	deleteLayout,
	setLayoutChangedModalInfo
} from '../context/projectLayout.js';
// import { setProjectFilterFromLayout } from '../context/projectFilter.js';
import { openAlert, openPrompt } from '../context/alert.js';

const LayoutSelection = ({
	setIsCapturingScreenshot,
	datasetInfo
}) => {
	const layoutSelectRef = useRef(null);
	const [{
		projectLayouts,
		projectLayoutId,
		projectFilter,
		projectViewType,
		projectGroupBy,
		projectGroupByActivityCode,
		projectMapGroupBy,
		layoutChanged
	}, dispatch] = useStateValue();

	const addLayoutHandler = () => {
		dispatch(openPrompt('Create a layout. Enter a layout name (max 255 characters)', name => {
			Utils.ajax(
				'addLayout',
				{
					dataset: datasetInfo.Id,
					name
				},
				dispatch,
				result => {
					dispatch(addLayout({result, name}));
				}
			);
		},
		'',
		'Layout name'));
	};

	const renameLayoutHandler = () => {
		dispatch(openPrompt('Rename layout (max 255 characters)', name => {
			Utils.ajax(
				'renameLayout',
				{ layoutId: projectLayoutId, name },
				dispatch,
				() => {
					dispatch(renameLayout(name));
				}
			);
		},
		'',
		'Rename layout'));
	};

	const deleteLayoutHandler = () => {
		dispatch(openAlert('Delete', 'Are you sure you want to delete this layout?', [{
			bsStyle: 'danger',
			text: 'Yes, delete',
			action: () => {
				Utils.ajax(
					'deleteLayout',
					{ layoutId: projectLayoutId },
					dispatch,
					() => {
						dispatch(deleteLayout());
					}
				);
			}
		}, {
			text: 'No, cancel',
			action: () => true
		}]));
	};

	const shareLayout = () => {
		dispatch(openPrompt('Share your layout. Enter email address', email => {
			if (email === '') {
				dispatch(openAlert('Email', 'You must enter an email address to share the layout.'));
				return;
			}
			setIsCapturingScreenshot(true);

			const mainDataComponent = document.getElementsByClassName('mainDataComponent')[0];
			html2canvas(mainDataComponent, { windowWidth: 550 }).then(canvas => {
				const imageData = canvas.toDataURL('image/png');
				setIsCapturingScreenshot(false);

				Utils.ajax(
					'shareLayout',
					{
						dataset: datasetInfo.Id,
						layoutId: projectLayoutId,
						email,
						imageData
					},
					dispatch,
					() => {
						dispatch(openAlert('Sharing layout succeeded', 'The current layout has been shared with ' + email));
					}
					// ,
					// fail: result => {
					// 	dispatch(openAlert('Sharing layout failed', result.error));
					// }
				);
			});
		},
		'',
		'Email address'));
	};

	const saveLayoutHandler = () => {
		const postData = {
			dataset: datasetInfo.Id,
			layoutId: projectLayoutId,
			filter: JSON.stringify(projectFilter),
			viewType: projectViewType,
			groupBy: projectGroupBy,
			groupByActivityCode: projectGroupByActivityCode,
			mapGroupBy: projectMapGroupBy
		};
		Utils.ajax(
			'saveLayout',
			postData,
			dispatch,
			() => {
				dispatch(saveLayout());
			}
		);
	};

	const handleChange = e => {
		const layoutId = e.target.value || -1;

		// Ask user if they want to save the layout and if so, to a new or existing one.
		if (projectLayoutId && layoutChanged) {
			dispatch(setLayoutChangedModalInfo({
				action: 'setLayout',
				changedFromLayoutId: projectLayoutId,
				changedToLayoutId: layoutId
			}));
			return;
		}

		dispatch(setLayout(layoutId));
		// dispatch(setProjectFilterFromLayout());
		const postData = {
			dataset: datasetInfo.Id,
			layoutId
		};
		Utils.ajax('setLayout', postData);
	};

	const choices = projectLayouts.map(layout => (
		<option key={layout.Id} value={layout.Id}>
			{(layout.Id === projectLayoutId && layoutChanged ? '* ' : '') + layout.Name}
		</option>
	));

	return (
		<Panel
			defaultExpanded
			className={classnames('layoutPanel', {activeFilter: projectLayoutId !== null})}>
			<Panel.Heading>
				<Panel.Title toggle>Layouts</Panel.Title>
			</Panel.Heading>
			<Panel.Collapse>
				<Panel.Body>
					<FormControl
						bsSize="small"
						componentClass="select"
						value={projectLayoutId || ''}
						onChange={handleChange}
						ref={layoutSelectRef}>
						<option value="">No filter</option>
						{choices}
					</FormControl>
					<div className="layoutButtonContainer">
						<Button
							bsSize="small"
							className="layoutButton"
							aria-label="Add layout"
							onClick={addLayoutHandler}>
							<span className="glyphicon glyphicon-plus-sign" title="Add" aria-hidden="true"></span>
						</Button>
						<Button
							bsSize="small"
							className="layoutButton"
							disabled={projectLayoutId === null}
							aria-label="Rename layout"
							onClick={renameLayoutHandler}>
							<span className="glyphicon glyphicon-pencil" title="Rename" aria-hidden="true"></span>
						</Button>
						<Button
							bsSize="small"
							className="layoutButton"
							disabled={projectLayoutId === null}
							aria-label="Save layout"
							onClick={saveLayoutHandler}>
							<span className="glyphicon glyphicon-floppy-save" title="Save" aria-hidden="true"></span>
						</Button>
						<Button
							bsSize="small"
							className="layoutButton"
							disabled={projectLayoutId === null}
							aria-label="Share layout"
							onClick={shareLayout}>
							<span className="glyphicon glyphicon-share" title="Share" aria-hidden="true"></span>
						</Button>
						<Button
							bsSize="small"
							className="layoutButton"
							bsStyle="danger"
							disabled={projectLayoutId === null}
							aria-label="Delete layout permanently"
							onClick={deleteLayoutHandler}>
							<span className="glyphicon glyphicon-trash" title="Delete" aria-hidden="true"></span>
						</Button>
					</div>
				</Panel.Body>
			</Panel.Collapse>
		</Panel>
	);
};

LayoutSelection.propTypes = {
	setIsCapturingScreenshot: PropTypes.func,
	datasetInfo: PropTypes.object
};

export default LayoutSelection;